import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { HERO_CANADA } from "@/lib/assets";
import { Mic, BookMarked, PenLine, MessageSquareQuote, ArrowRight, Check, ShieldCheck, Sparkles } from "lucide-react";

const coaches = [
  { icon: Mic, name: "Speaking Coach", desc: "Voice-ready partner for all 3 TCF speaking tasks with examiner feedback." },
  { icon: BookMarked, name: "Vocabulary Coach", desc: "Learn the exact words examiners reward, with Hindi memory tricks." },
  { icon: PenLine, name: "Sentence Builder", desc: "Build French sentences you'll actually remember under exam pressure." },
  { icon: MessageSquareQuote, name: "Point of View Coach", desc: "Master Tâche 3 with the PEEL method and score B1 → B2." },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user && user !== false) navigate("/dashboard"); }, [user, navigate]);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden grain" data-testid="landing-page">
      <header className="relative z-10 max-w-6xl mx-auto px-5 h-20 flex items-center justify-between">
        <Logo size={40} textClass="text-2xl" />
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="landing-login-btn" className="px-4 py-2 rounded-full text-sm font-semibold hover:bg-secondary transition-colors">Log in</Link>
          <Link to="/register" data-testid="landing-register-btn" className="px-5 py-2 rounded-full text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity">Get started</Link>
        </div>
      </header>

      <section className="relative z-10 max-w-6xl mx-auto px-5 pt-10 pb-24 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent text-accent-foreground text-xs font-semibold mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" /> TCF Canada · AI-powered prep
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-semibold leading-[1.04] tracking-tight">
            Crack the TCF,
            <span className="block text-primary">with a coach in your pocket.</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-md leading-relaxed">
            All In One French gives learners four AI coaches — speaking, vocabulary, sentences and opinion — plus real exam topics, all in warm Hinglish.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/register" data-testid="hero-cta-btn" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity">
              Start your free day <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/login" className="px-6 py-3.5 rounded-full font-semibold border border-border hover:bg-secondary transition-colors">I have an account</Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
            {["4 AI coaches", "Voice speaking practice", "1 day free"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-primary" />{t}</span>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 }} className="relative">
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border border-border/60">
            <img src={HERO_CANADA} alt="Canada" className="w-full h-[460px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/45 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 glass rounded-2xl px-5 py-4 border border-white/30">
              <div className="flex items-center gap-2 text-white"><Sparkles className="h-4 w-4" /><span className="font-semibold">Your goal: land in Canada 🇨🇦</span></div>
            </div>
          </div>
          <div className="absolute -top-5 -right-4 glass rounded-2xl px-4 py-3 border border-border shadow-xl">
            <div className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4 text-primary" /> Encrypted & private</div>
          </div>
        </motion.div>
      </section>

      <section className="relative z-10 max-w-6xl mx-auto px-5 pb-28">
        <h2 className="font-display text-3xl font-semibold mb-2">Meet your coaches</h2>
        <p className="text-muted-foreground mb-10">Four specialists, available 24/7, tuned to how TCF examiners actually score.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {coaches.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div key={c.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-2xl bg-card border border-border p-6 hover:shadow-lg hover:-translate-y-1 transition-all">
                <div className="h-11 w-11 rounded-xl emerald-surface grid place-items-center mb-4"><Icon className="h-5 w-5 text-white" /></div>
                <h3 className="font-semibold text-lg mb-1">{c.name}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
